// 비콘 태그리스 감시 — 네이티브 `CapacitorIbeacon` 브리지 (2026-10-11 · 설계 §3 흐름)
// Firebase import 금지 — 탑승 호출(CF boardTagless)은 호출부가 onCandidate 에서 한다.
//
// 흐름: 공용 UUID 영역 감시(startMonitoringForRegion) → 진입하면 ranging 시작 →
//       createDwellTracker 가 같은 비콘을 dwell 이상 rssi 기준으로 보면 onCandidate 1회 → 이탈하면 ranging 중지.
// 🔴 옵트인 기본 꺼짐 — 승객이 설정에서 켠 기기에서만 돈다(localStorage · 기기별 설정이라 서버에 안 둔다).
// 🔴 플러그인이 없는 앱·브라우저에서는 아무것도 하지 않는다(hasNativePlugin false → 버튼도 숨김).
import { hasNativePlugin, nativeCall, nativeListen } from "./nativeApp";
import { BUSLINK_BEACON_UUID, createDwellTracker } from "./taglessPolicy";

export const IBEACON_PLUGIN = "CapacitorIbeacon"; // @capgo/capacitor-ibeacon 의 registerPlugin 이름(소스 실측)
const OPT_IN_KEY = "buslink_tagless_optin_v1";
const REGION = { identifier: "buslink-bus", uuid: BUSLINK_BEACON_UUID };

export function isTaglessAvailable() {
  return hasNativePlugin(IBEACON_PLUGIN);
}

/** 옵트인 여부. 🔴 기본 false · 저장소 접근 실패도 false. 사번별로 나눠 둔다(공용 폰에서 남의 설정을 물려받지 않게). */
export function isTaglessOptedIn(empNo) {
  try { return localStorage.getItem(OPT_IN_KEY) === String(empNo || ""); } catch { return false; }
}
export function setTaglessOptIn(empNo, on) {
  try {
    if (on) localStorage.setItem(OPT_IN_KEY, String(empNo || ""));
    else localStorage.removeItem(OPT_IN_KEY);
  } catch (_) { /* 저장 실패 = 다음 실행에 꺼짐(안전한 쪽) */ }
}

function platform() {
  try { return window.Capacitor && window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : ""; } catch { return ""; }
}

/**
 * 켤 때 1회 — 권한 요청. iOS = 위치 «항상» · Android = 백그라운드 모드(포그라운드 서비스).
 * @returns {Promise<{ok:boolean, message?:string}>}
 */
export async function requestTaglessPermission() {
  try {
    if (platform() === "ios") {
      const r = await nativeCall(IBEACON_PLUGIN, "requestAlwaysAuthorization");
      const st = String((r && r.status) || "");
      if (/denied|restricted/i.test(st)) return { ok: false, message: "위치 권한이 «항상 허용» 이어야 자동 탑승이 됩니다" };
    } else {
      try { await nativeCall(IBEACON_PLUGIN, "requestWhenInUseAuthorization"); } catch (_) { /* 안드로이드는 플러그인이 권한을 묻는다 */ }
      await nativeCall(IBEACON_PLUGIN, "enableBackgroundMode", { enabled: true });
    }
    try {
      const bt = await nativeCall(IBEACON_PLUGIN, "isBluetoothEnabled");
      if (bt && bt.enabled === false) return { ok: false, message: "블루투스를 켜 주세요" };
    } catch (_) { /* 확인 실패는 막지 않는다 */ }
    return { ok: true };
  } catch (e) {
    return { ok: false, message: (e && e.message) || "권한을 받지 못했습니다" };
  }
}

/**
 * 감시 시작. onCandidate({major,minor,rssi,dwellSec}) 는 dwell 조건을 채웠을 때만.
 * @returns {() => void} 정지 함수(리스너·감시·ranging 모두 정리)
 */
export function startTaglessWatcher({ onCandidate, now = () => Date.now() }) {
  if (!isTaglessAvailable()) return () => {};
  const tracker = createDwellTracker();
  const subs = [];
  let ranging = false;
  let stopped = false;
  const startRanging = () => {
    if (ranging || stopped) return;
    ranging = true;
    nativeCall(IBEACON_PLUGIN, "startRangingBeaconsInRegion", REGION).catch(() => { ranging = false; });
  };
  const stopRanging = () => {
    if (!ranging) return;
    ranging = false;
    tracker.reset();
    nativeCall(IBEACON_PLUGIN, "stopRangingBeaconsInRegion", REGION).catch(() => {});
  };
  subs.push(nativeListen(IBEACON_PLUGIN, "didEnterRegion", startRanging));
  subs.push(nativeListen(IBEACON_PLUGIN, "didExitRegion", stopRanging));
  subs.push(nativeListen(IBEACON_PLUGIN, "didDetermineStateForRegion", (e) => {
    if (e && e.state === "enter") startRanging(); else if (e && e.state === "exit") stopRanging();
  }));
  subs.push(nativeListen(IBEACON_PLUGIN, "didRangeBeacons", (e) => {
    if (stopped) return;
    const hit = tracker.feed(e && e.beacons, now());
    if (hit) onCandidate && onCandidate(hit);
  }));
  nativeCall(IBEACON_PLUGIN, "startMonitoringForRegion", { ...REGION, notifyEntryStateOnDisplay: true }).catch(() => {});
  return () => {
    stopped = true;
    subs.forEach((s) => s && s.remove());
    stopRanging();
    nativeCall(IBEACON_PLUGIN, "stopMonitoringForRegion", REGION).catch(() => {});
  };
}
